import { useEffect, useRef, useState } from 'react';
import { imageUrls, MAX_IMAGES, uploadImage, type AssignmentImage } from '../lib/assignmentImages';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's "Images" box on the assignment form: add pictures (choose
// files, drag them in, or paste), give each a caption, put them in order.
// Uploads happen right away; the list is saved with the assignment.
// ─────────────────────────────────────────────────────────────────────────────

type Props = { classroomId: string; images: AssignmentImage[]; onChange: (next: AssignmentImage[]) => void };

export default function ImageAttach({ classroomId, images, onChange }: Props) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    const paths = images.map(i => i.path).filter(p => !urls[p]);
    if (paths.length) imageUrls(paths).then(u => setUrls(prev => ({ ...prev, ...u })));
  }, [images]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (files: File[]) => {
    const pics = files.filter(f => f.type.startsWith('image/'));
    if (!pics.length) return;
    const room = MAX_IMAGES - imagesRef.current.length;
    if (room <= 0) { setError(`${MAX_IMAGES} images at most.`); return; }
    setError('');
    const take = pics.slice(0, room);
    if (pics.length > room) setError(`Only ${room} more fit (${MAX_IMAGES} at most).`);
    setUploading(n => n + take.length);
    for (const f of take) {
      try {
        const img = await uploadImage(classroomId, f);
        // Show the picture straight from the computer while the signed link comes.
        setUrls(prev => ({ ...prev, [img.path]: URL.createObjectURL(f) }));
        onChange([...imagesRef.current, img]);
      } catch (e) { setError(e instanceof Error ? e.message : 'Could not upload that picture.'); }
      setUploading(n => n - 1);
    }
  };

  // Paste a picture anywhere on the form.
  useEffect(() => {
    const p = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])].filter(f => f.type.startsWith('image/'));
      if (files.length) { e.preventDefault(); void add(files); }
    };
    window.addEventListener('paste', p);
    return () => window.removeEventListener('paste', p);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (i: number, d: number) => {
    const j = i + d; if (j < 0 || j >= images.length) return;
    const next = [...images]; [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };

  return (
    <div className="field">
      <label>Images (optional)</label>
      <div className={`img-attach${over ? ' over' : ''}`}
        onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); void add([...e.dataTransfer.files]); }}>
        {images.length > 0 && (
          <div className="img-attach-grid">
            {images.map((img, i) => (
              <div key={img.path} className="img-attach-item">
                {urls[img.path] ? <img src={urls[img.path]} alt={img.caption ?? ''} /> : <div className="img-attach-wait">…</div>}
                <input value={img.caption ?? ''} maxLength={200} placeholder="Caption (optional)" dir="auto"
                  onChange={e => onChange(images.map((x, k) => (k === i ? { ...x, caption: e.target.value } : x)))} />
                <div className="row img-attach-tools">
                  <button type="button" className="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move earlier">←</button>
                  <button type="button" className="small" disabled={i === images.length - 1} onClick={() => move(i, 1)} aria-label="Move later">→</button>
                  <span className="spacer" />
                  <button type="button" className="small" onClick={() => onChange(images.filter((_, k) => k !== i))} aria-label="Remove">✕ Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="row img-attach-add">
          <button type="button" disabled={images.length >= MAX_IMAGES} onClick={() => fileRef.current?.click()}>🖼️ Add images</button>
          <span className="hint">{uploading ? `Uploading ${uploading}…` : `Or drag pictures here, or paste one. Up to ${MAX_IMAGES}; students see them on the assignment.`}</span>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => { void add([...(e.target.files ?? [])]); e.target.value = ''; }} />
        </div>
      </div>
      {error && <span className="hint warn">{error}</span>}
    </div>
  );
}
